/**
 * Stats page — premium aggregate view over send_log with sharing.
 *
 * Sections:
 *   1. Header (back / "Поделиться")
 *   2. Range chips: 24ч / 7д / 30д
 *   3. Hero card — total sent + delta vs previous window + lifetime
 *   4. Time-series chart (SVG)
 *   5. Status pills (sent / failed / skipped)
 *   6. Top chats
 *   7. Top accounts
 *
 * Route: /app/stats
 */

import { motion } from "framer-motion";
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Crown,
  MessageSquare,
  Pause,
  Share2,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useStatsOverview,
  useStatsTopAccounts,
  useStatsTopChats,
  type StatsOverviewBucket,
} from "@/api/stats";
import { ShareSheet } from "@/components/stats/ShareSheet";

const RANGES = [
  { days: 1, label: "24ч" },
  { days: 7, label: "7д" },
  { days: 30, label: "30д" },
] as const;

export function StatsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState<number>(7);
  const [shareOpen, setShareOpen] = useState(false);

  const { data: overview, isLoading } = useStatsOverview(days);
  const { data: topChats } = useStatsTopChats({ days, limit: 10 });
  const { data: topAccounts } = useStatsTopAccounts({ days, limit: 10 });

  const series = overview?.series ?? [];
  const sent = overview?.sent ?? 0;
  const failed = overview?.failed ?? 0;
  const skipped = overview?.skipped ?? 0;
  const delta = overview?.delta_sent_pct ?? 0;

  const successRate = useMemo(() => {
    const total = sent + failed;
    if (total === 0) return 0;
    return Math.round((sent / total) * 100);
  }, [sent, failed]);

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate("/app");
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Статистика</div>
        <button
          onClick={() => {
            haptic.impact("light");
            setShareOpen(true);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Поделиться"
        >
          <Share2 className="size-4" />
        </button>
      </div>

      {/* Range chips */}
      <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-white/[0.04] border border-white/[0.05]">
        {RANGES.map((r) => {
          const active = days === r.days;
          return (
            <button
              key={r.days}
              onClick={() => {
                setDays(r.days);
                haptic.select();
              }}
              className={cn(
                "relative flex-1 h-9 rounded-xl text-[13px] font-medium transition-colors",
                active ? "text-white" : "text-text-muted hover:text-text",
              )}
            >
              {active && (
                <motion.div
                  layoutId="range-pill"
                  className="absolute inset-0 rounded-xl bg-accent-soft border border-accent/30"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <span className="relative">{r.label}</span>
            </button>
          );
        })}
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="relative p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
                  Отправлено за период
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-[42px] leading-none font-semibold tabular-nums tracking-tight">
                    {fmtNum(sent)}
                  </span>
                  <DeltaBadge value={delta} />
                </div>
                <div className="text-xs text-text-dim mt-2">
                  Всего за всё время:{" "}
                  <span className="text-text-muted tabular-nums">
                    {fmtNum(overview?.lifetime_sent ?? 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Chart */}
            <SeriesChart series={series} loading={isLoading} />
          </div>
        </Card>
      </motion.div>

      {/* Status pills */}
      <div className="grid grid-cols-3 gap-2.5">
        <StatusPill
          icon={<CheckCircle2 className="size-3.5" />}
          label="Успех"
          value={sent}
          tone="success"
        />
        <StatusPill
          icon={<XCircle className="size-3.5" />}
          label="Ошибки"
          value={failed}
          tone="danger"
        />
        <StatusPill
          icon={<Pause className="size-3.5" />}
          label="Пропуски"
          value={skipped}
          tone="neutral"
        />
      </div>

      {/* Success rate */}
      <Card padded>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-lg bg-accent-soft text-accent grid place-items-center">
              <TrendingUp className="size-4" />
            </div>
            <div>
              <div className="text-[13px] font-medium">Качество отправки</div>
              <div className="text-[11px] text-text-muted">
                Успешных из всех попыток
              </div>
            </div>
          </div>
          <div className="text-2xl font-semibold tabular-nums">
            {successRate}
            <span className="text-base text-text-muted">%</span>
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${successRate}%` }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="h-full bg-gradient-accent rounded-full"
          />
        </div>
      </Card>

      {/* Top chats */}
      <Section
        title="Топ чатов"
        icon={<MessageSquare className="size-3" />}
        empty={topChats != null && topChats.items.length === 0}
        emptyText="Пока пусто — запустите рассылку"
      >
        {(topChats?.items ?? []).map((it, idx) => (
          <TopRow
            key={`c-${it.chat_id ?? idx}`}
            rank={idx + 1}
            title={it.chat_title ?? `Чат #${it.chat_id ?? "?"}`}
            subtitle={`${it.sent} успехов · ${it.failed} ошибок`}
            value={it.sent}
            max={topChats?.items[0]?.sent ?? 1}
          />
        ))}
      </Section>

      {/* Top accounts */}
      <Section
        title="Топ аккаунтов"
        icon={<Users className="size-3" />}
        empty={topAccounts != null && topAccounts.items.length === 0}
        emptyText="Пока пусто — добавьте аккаунт"
      >
        {(topAccounts?.items ?? []).map((it, idx) => (
          <TopRow
            key={`a-${it.account_id ?? idx}`}
            rank={idx + 1}
            title={it.name ?? it.phone ?? `Аккаунт #${it.account_id ?? "?"}`}
            subtitle={`${it.sent} успехов · ${it.failed} ошибок`}
            value={it.sent}
            max={topAccounts?.items[0]?.sent ?? 1}
          />
        ))}
      </Section>

      <ShareSheet open={shareOpen} onClose={() => setShareOpen(false)} days={days} />
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function DeltaBadge({ value }: { value: number }) {
  const zero = value === 0;
  const up = value > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums px-1.5 py-0.5 rounded-md",
        zero && "bg-white/[0.04] text-text-dim",
        !zero && up && "bg-success/15 text-success",
        !zero && !up && "bg-danger/15 text-danger",
      )}
    >
      {!zero && up && <ArrowUpRight className="size-3" />}
      {!zero && !up && <ArrowDownRight className="size-3" />}
      {zero ? "—" : `${up ? "+" : ""}${value}%`}
    </span>
  );
}

function StatusPill({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: "success" | "danger" | "neutral";
}) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div
        className={cn(
          "size-6 rounded-lg grid place-items-center",
          tone === "success" && "bg-success/15 text-success",
          tone === "danger" && "bg-danger/15 text-danger",
          tone === "neutral" && "bg-white/[0.04] text-text-dim",
        )}
      >
        {icon}
      </div>
      <div className="mt-2 text-[10px] text-text-dim uppercase tracking-wider font-medium">
        {label}
      </div>
      <div className="text-[22px] leading-none font-semibold tabular-nums tracking-tight mt-0.5">
        {fmtNum(value)}
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
  empty,
  emptyText,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  empty?: boolean;
  emptyText: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs text-text-muted uppercase tracking-wider font-medium px-1">
        <span className="text-text-dim">{icon}</span>
        {title}
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
        {empty ? (
          <div className="px-4 py-10 text-center text-xs text-text-muted">{emptyText}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function TopRow({
  rank,
  title,
  subtitle,
  value,
  max,
}: {
  rank: number;
  title: string;
  subtitle: string;
  value: number;
  max: number;
}) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const isTop = rank === 1;
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div
        className={cn(
          "size-8 rounded-lg grid place-items-center shrink-0 text-[12px] font-semibold tabular-nums",
          isTop ? "bg-accent-soft text-accent" : "bg-white/[0.04] text-text-dim",
        )}
      >
        {isTop ? <Crown className="size-3.5" /> : rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-medium truncate">{title}</div>
          <div className="text-[13px] font-semibold tabular-nums shrink-0">
            {fmtNum(value)}
          </div>
        </div>
        <div className="text-[11px] text-text-muted truncate mt-0.5">{subtitle}</div>
        <div className="mt-2 h-1 rounded-full bg-white/[0.04] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              "h-full rounded-full",
              isTop ? "bg-gradient-accent" : "bg-white/15",
            )}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SVG chart — pure CSS / SVG, no chart library
// ============================================================

function SeriesChart({
  series,
  loading,
}: {
  series: StatsOverviewBucket[];
  loading?: boolean;
}) {
  const w = 300;
  const h = 100;
  const pad = 6;

  const safe = series.length > 0 ? series : [{ ts: "", sent: 0, failed: 0 }];
  const max = Math.max(1, ...safe.map((p) => p.sent + p.failed));
  const barW = (w - pad * 2) / safe.length;
  const innerW = barW * 0.6;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${w} ${h + 16}`}
        className={cn("w-full", loading && "opacity-50")}
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="bar-sent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF6B35" stopOpacity="1" />
            <stop offset="100%" stopColor="#FF3D00" stopOpacity="0.75" />
          </linearGradient>
        </defs>
        {/* zero-line */}
        <line
          x1={pad}
          x2={w - pad}
          y1={h}
          y2={h}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={1}
        />
        {safe.map((p, i) => {
          const x = pad + i * barW + (barW - innerW) / 2;
          const total = p.sent + p.failed;
          const totalH = total > 0 ? ((h - 4) * total) / max : 0;
          const sentH = total > 0 ? (totalH * p.sent) / total : 0;
          const failedH = totalH - sentH;
          const yBase = h;
          return (
            <g key={i}>
              {/* failed segment (bottom) */}
              {failedH > 0 && (
                <rect
                  x={x}
                  y={yBase - failedH}
                  width={innerW}
                  height={failedH}
                  fill="rgba(255,77,109,0.6)"
                  rx={1.5}
                />
              )}
              {/* sent segment (on top) */}
              {sentH > 0 && (
                <rect
                  x={x}
                  y={yBase - failedH - sentH}
                  width={innerW}
                  height={sentH}
                  fill="url(#bar-sent)"
                  rx={1.5}
                />
              )}
              {/* tiny dot for zero-rows so the day still reads */}
              {total === 0 && (
                <circle
                  cx={x + innerW / 2}
                  cy={yBase}
                  r={0.8}
                  fill="rgba(255,255,255,0.15)"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-2 flex items-center justify-between text-[10px] text-text-dim">
        <div className="flex items-center gap-3">
          <Legend color="#FF6B35" label="Успех" />
          <Legend color="rgba(255,77,109,0.7)" label="Ошибки" />
        </div>
        <div className="text-text-faint">
          {series.length > 0
            ? `${formatDayShort(series[0].ts)} → ${formatDayShort(series[series.length - 1].ts)}`
            : ""}
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span
        className="inline-block size-2 rounded-sm"
        style={{ background: color }}
        aria-hidden
      />
      {label}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function fmtNum(n: number): string {
  return n.toLocaleString("ru-RU");
}

function formatDayShort(iso: string): string {
  // expect YYYY-MM-DD
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}`;
}
